import { View } from 'rune-ts';
import { Input_TextEditor } from '@services/MarppleShopStudio/shared/component/atom/Input/Input_TextEditor.ts';
import { formKeyMap } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import { createCreatorProductStore } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/createStore.ts';
import { subscribeStore } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/helper.ts';
import { Input_RadioGroup } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_RadioGroup.ts';
import {
  CreatorProductDefaultData,
  MODE,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import { Input_Category } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Category.ts';
import { Input_Price } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Price.ts';
import { MONEY_SIGN } from '@services/MarppleShop/shared/constant';
import { MembershipSettingSection } from '@modules/Creator/Setting/S/MembershipSettingForm/MembershipSettingSection.ts';
import { isMembershipSupported } from '@modules/Util/S/Function/membership.ts';
import {
  MultiSelectButtonGroup,
  MultiSelectButtonGroupClickEvent,
} from '@services/MarppleShop/renderApp/components/cells/MultiSelectButtonGroup/MultiSelectButtonGroup.ts';
import { BaseSection } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/BaseSection.ts';
import { compact, pipe, toArray } from '@fxts/core';
import { EditorInputFocusoutEvent } from '@services/MarppleShopStudio/shared/component/atom/Input/core/events.ts';

interface MainInfoSectionData<T extends MODE> {
  store: ReturnType<typeof createCreatorProductStore<T>>;
  mpsCategories: CreatorProductDefaultData['mpsCategories'];
  products_memberships: CreatorProductDefaultData['products_memberships'];
}

export class MainInfoSection<T extends MODE> extends BaseSection<T, MainInfoSectionData<T>> {
  private store = this.data.store;
  protected readonly sectionTitle = '대표 정보';

  private readonly is_membership_section_visible = !!(
    isMembershipSupported() && this.data.products_memberships?.length
  );

  private inputMap = {
    [formKeyMap.is_public]: new Input_RadioGroup({
      required: true,
      name: formKeyMap.is_public,
      is_public: this.store.formData._.product.is_public ?? false,
      items: [
        { id: 'true', display_name: '공개', value: true },
        { id: 'false', display_name: '비공개', value: false },
      ],
      title: '공개 상태',
    }),
    [formKeyMap.product_name]: new Input_TextEditor({
      required: true,
      name: formKeyMap.product_name,
      value: this.store.formData._.product.name ?? '',
      title: '상품명',
      option: {
        max_length: 100,
        placeholder: '예) 반려동물 뜨개 케이프',
      },
    }),
    [formKeyMap.profit]: new Input_Price({
      required: true,
      name: formKeyMap.profit,
      value: this.store.formData._.product.profit,
      title: '소비자 가격',
      sign: MONEY_SIGN.kr,
      min: 0,
      max: 99_999_999,
    }),
  };

  private categoryInput = new Input_Category({
    mpsCategories: this.data.mpsCategories,
    sp_cate_root_code: this.store.formData.sp_cate_root_code,
    sp_cate_list_code: this.store.formData.sp_cate_list_code,
    sp_cate_item_code: this.store.formData.sp_cate_item_code,
    setField: (keyName, value) => {
      this.data.store.actions.setField(keyName, value);
    },
  });

  protected override inputViews(): Array<View> {
    return pipe(
      [
        this.inputMap.is_public,
        this.inputMap.product_name,
        this.categoryInput,
        this.inputMap.profit,
        this.is_membership_section_visible
          ? new MembershipSettingSection({
              memberships: this.data.products_memberships ?? [],
            })
          : null,
      ],
      compact,
      toArray,
    );
  }

  override onRender() {
    this.addEventListener(EditorInputFocusoutEvent, (e) => {
      if (this.isValidInputKey(e.detail.name)) {
        const view = this.inputMap[e.detail.name];
        view && this.data.store.actions.setField(view.getName(), view.getValue());
      }
    });

    this.addEventListener(this.inputMap.is_public.changeEvent, (e) => {
      const selected = e.detail;
      if (selected) {
        const view = this.inputMap.is_public;
        view && this.data.store.actions.setField(view.getName(), view.getValue());
      }
    });

    this.delegate(MultiSelectButtonGroupClickEvent, MultiSelectButtonGroup<string>, (e) => {
      const membership_ids = e.detail.selected.map(({ id }) => String(id));
      this.data.store.actions.setField(formKeyMap.membership_ids, membership_ids);
    });

    subscribeStore(this.data.store, this.inputMap);
  }

  private isValidInputKey(key: string): key is keyof typeof this.inputMap {
    return key in this.inputMap;
  }
}
